import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { wsManager } from "../../websocket/wsManager";
import { formatTime } from "../../utils/date";
import { Link } from "react-router-dom";
import { Button, Dropdown, Image, Modal } from "react-bootstrap";
import EntityShareCard from "./EntityShareCard";
import { getPublicProfileHref } from "../../utils/publicProfile";

// Default wsManager.send({ queue: true }) contract:
//   true  → delivered on open socket
//   false → accepted into reconnect pendingMessages queue (NOT failure)
const WS_QUEUED_TOAST =
  "Зміну буде надіслано після відновлення з’єднання.";
const WS_SEND_EXCEPTION_TOAST =
  "Не вдалося надіслати зміну. Перевірте з'єднання.";

function isImageOnlyMessage(msg) {
  if (!msg || !msg.image) return false;
  if (msg.file || msg.shared_entity) return false;
  if (typeof msg.text === "string") return msg.text.trim() === "";
  return !msg.text;
}

export default function MessageGroup({ group, currentUserId, onImageClick }) {
  const isMine = parseInt(group.sender?.id) === parseInt(currentUserId);

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editText, setEditText] = useState("");
  // Synchronous re-entry guard for the visible modal action only.
  // wsManager.send is synchronous; there is no async network pending
  // interval and no server acknowledgement awaited here (R1-2).
  const submitGuardRef = useRef(false);

  const closeMessageModal = () => {
    setSelectedMessage(null);
  };

  const acceptMutation = (sent, successToast) => {
    // Immediate (true) or queued (false): both are accepted. Close the
    // modal so the user cannot enqueue the same action again.
    setSelectedMessage(null);
    if (sent) {
      toast.success(successToast);
      return;
    }
    toast.info(WS_QUEUED_TOAST);
  };

  const handleEditText = (msg) => {
    if (submitGuardRef.current) return;
    if (!editText.trim()) return;
    submitGuardRef.current = true;
    try {
      const sent = wsManager.send({
        event: "message_edit",
        message_id: msg.id,
        text: editText,
      });
      // Accepted (immediate or queued): keep guard latched so a second
      // click on the still-mounted modal control cannot duplicate-send.
      acceptMutation(Boolean(sent), "Повідомлення оновлено.");
    } catch (_err) {
      submitGuardRef.current = false;
      toast.error(WS_SEND_EXCEPTION_TOAST);
    }
  };

  const handleDelete = (msg) => {
    if (submitGuardRef.current) return;
    submitGuardRef.current = true;
    try {
      const sent = wsManager.send({
        event: "message_delete",
        message_id: msg.id,
      });
      acceptMutation(Boolean(sent), "Повідомлення видалено.");
    } catch (_err) {
      submitGuardRef.current = false;
      toast.error(WS_SEND_EXCEPTION_TOAST);
    }
  };

  return (
    <div className={isMine ? 'main-user' : 'second-user'}>
      {!isMine && (
        <Link
          to={getPublicProfileHref(group.sender) || "#"}
          className="message-user-img"
        >
          <span className="message-user-icon">
            <Image
              src={group.sender.user_image ? group.sender.user_image : "/images/no-profile-picture.png"}
              alt={group.sender.fullname ? group.sender.fullname : group.sender.email}
            />
          </span>
        </Link>
      )}
      {(isMine && group.messages.length > 0 && selectedMessage) && (
        <Modal
          show={selectedMessage !== null}
          onHide={closeMessageModal}
          aria-labelledby="contained-modal-title-vcenter"
          centered
          className="project-modal"
        >
          <Modal.Header>
            <Modal.Title className={selectedMessage?.event === "delete" ? "text-danger" : ""}>
              {selectedMessage?.event === "edit" ? "Редагувати повідомлення"
                : selectedMessage?.event === "delete" ? "Видалити повідомлення?" : ""}
            </Modal.Title>
            <div
              className="modal-btn-close"
              onClick={closeMessageModal}
              role="button"
              tabIndex={0}
              aria-label="Закрити"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") closeMessageModal();
              }}
            >
              <i className="fa-solid fa-xmark fa-2x"></i>
            </div>
          </Modal.Header>
          {selectedMessage?.event === "edit" && (
            <Modal.Body>
              <textarea
                className="form-control w-100"
                id={`chat-group-edit-text-${selectedMessage?.msg?.id || "draft"}`}
                placeholder="Повідомлення"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleEditText(selectedMessage?.msg);
                  }
                }}
              ></textarea>
            </Modal.Body>
          )}
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeMessageModal}
            >
              Закрити
            </Button>
            {selectedMessage?.event === "edit" && (
              <Button
                variant="primary"
                onClick={() => handleEditText(selectedMessage?.msg)}
                disabled={!editText.trim()}
              >
                Зберегти зміни
              </Button>
            )}
            {selectedMessage?.event === "delete" && (
              <Button
                variant="danger"
                onClick={() => handleDelete(selectedMessage?.msg)}
              >
                Видалити
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
      <div className="group-message">
        {group.messages.map((msg, index) => {
          const isFirst = index === 0;
          const isLast = index === group.messages.length - 1;
          if (msg.is_deleted) {
            return (
              <div className="message-content" key={msg.id}>
                <div className={`text text-danger ${(isFirst && isLast) ? "first-with-tail" : ""} ${(isFirst && !isLast) ? "first" : ""} ${(!isFirst && isLast) ? "with-tail" : ""}`}>
                  Повідомлення було видалене!
                  <div className="message-info">
                    <div className="time">{formatTime(msg.created_at)}</div>
                  </div>
                </div>
              </div>
            )
          }
          return (
            <div className="message-content" key={msg.id}>

              {isMine && !msg.shared_entity && (
                <Dropdown className="message-setting">
                  <Dropdown.Toggle
                    as="i"
                    className="fa-solid fa-ellipsis message-setting__toggle"
                    id={String(msg.id)}
                    aria-label="Дії з повідомленням"
                  />
                  <Dropdown.Menu className="message-setting__menu">
                    <Dropdown.Item
                      className="message-setting__item"
                      onClick={() => {
                        submitGuardRef.current = false;
                        setSelectedMessage({ event: "edit", msg });
                        setEditText(msg.text);
                      }}
                    >
                      <i className="fa-solid fa-pen message-setting__item-icon" aria-hidden="true" />
                      Редагувати
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        submitGuardRef.current = false;
                        setSelectedMessage({ event: "delete", msg });
                      }}
                      className="text-danger message-setting__danger message-setting__item"
                    >
                      <i className="fa-solid fa-trash message-setting__item-icon" aria-hidden="true" />
                      Видалити
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {isMine && msg.shared_entity && (
                <Dropdown className="message-setting">
                  <Dropdown.Toggle
                    as="i"
                    className="fa-solid fa-ellipsis message-setting__toggle"
                    id={String(msg.id)}
                    aria-label="Дії з повідомленням"
                  />
                  <Dropdown.Menu className="message-setting__menu">
                    <Dropdown.Item
                      onClick={() => {
                        submitGuardRef.current = false;
                        setSelectedMessage({ event: "delete", msg });
                      }}
                      className="text-danger message-setting__danger message-setting__item"
                    >
                      <i className="fa-solid fa-trash message-setting__item-icon" aria-hidden="true" />
                      Видалити
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <div className={`text ${(isFirst && isLast) ? "first-with-tail" : ""} ${(isFirst && !isLast) ? "first" : ""} ${(!isFirst && isLast) ? "with-tail" : ""} ${isImageOnlyMessage(msg) ? "text--image-only" : ""}`}>
                {msg.image && (
                  <div className="chat-send-img">
                    <button
                      type="button"
                      className="chat-send-img__trigger"
                      data-testid={`chat-send-img-trigger-${msg.id}`}
                      onClick={(event) => onImageClick?.(msg.id, event.currentTarget)}
                      aria-label={
                        msg.image_name
                          ? `Відкрити зображення ${msg.image_name}`
                          : "Відкрити зображення"
                      }
                    >
                      <Image
                        src={msg.image}
                        alt={msg.image_name || ""}
                        className="chat-send-img__media"
                      />
                    </button>
                    <Link
                      to={msg.image}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chat-send-img__download"
                      aria-label="Завантажити зображення"
                      title="Завантажити зображення"
                      download
                    >
                      <i className="fa-solid fa-download" aria-hidden="true" />
                    </Link>
                  </div>
                )}
                {msg.file && (
                  <div className="chat-send-file" key={msg.id}>
                    {msg.file_name.split('.').at(-1) === 'xlsx' ? (
                      <Image src="/images/xlsx.png" />
                    ) : msg.file_name.split('.').at(-1) === 'accdb' ? (
                      <Image src="/images/accdb.png" />
                    ) : msg.file_name.split('.').at(-1) === 'docx' ? (
                      <Image src="/images/docx.png" />
                    ) : msg.file_name.split('.').at(-1) === 'pptx' ? (
                      <Image src="/images/pptx.png" />
                    ) : msg.file_name.split('.').at(-1) === 'pub' && (
                      <Image src="/images/pub.png" />
                    )}
                    {msg.file_name ? (
                      <div className="file-name">{msg.file_name}</div>
                    ) : (
                      <div className="file-name">{msg.file.split('/').at(-1)}</div>
                    )}
                    <Link
                      to={msg.file}
                      className="download-file chat-send-file__download"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      aria-label="Завантажити файл"
                      title="Завантажити файл"
                    >
                      <i className="fa-solid fa-download"></i>
                    </Link>
                  </div>
                )}
                {msg.shared_entity && (
                  <EntityShareCard sharedEntity={msg.shared_entity} />
                )}
                {msg.text}
                <div className="message-info">
                  {msg.edited && (
                    <div className="edited">Редаг.</div>
                  )}
                  <div className="time">{formatTime(msg.created_at)}</div>
                  {isMine && (msg.seen_by?.length > 0 ? (
                    <i className="fa-solid fa-eye"></i>
                  ) : (
                    <i className="fa-regular fa-eye"></i>
                  ))}
                </div>
                {msg.pending && <em> ⏳</em>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  );
}
